import { useState, useEffect, useCallback } from 'react';
import cashSessionService from '../services/cashSessionService';

/**
 * Hook personalizado para manejar la sesión de caja activa
 */
export const useCashSession = (cashRegisterId = null) => {
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Cargar la sesión activa
   */
  const loadActiveSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await cashSessionService.getActive(cashRegisterId);
      
      if (response.success && response.data) {
        setActiveSession(response.data);
      } else {
        setActiveSession(null);
      }
    } catch (err) {
      console.error('Error loading active session:', err);
      setError(err.response?.data?.message || 'Error al cargar la sesión activa');
      setActiveSession(null);
    } finally {
      setLoading(false);
    }
  }, [cashRegisterId]);

  /**
   * Abrir una nueva sesión
   */
  const openSession = async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await cashSessionService.open(data);
      
      if (response.success) {
        setActiveSession(response.data);
        return { success: true, data: response.data };
      }
      
      return { success: false, message: 'Error al abrir sesión' };
    } catch (err) {
      console.error('Error opening session:', err);
      const errorMsg = err.response?.data?.message || 'Error al abrir la sesión';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cerrar la sesión activa
   */
  const closeSession = async (sessionId, data) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await cashSessionService.close(sessionId, data);
      
      if (response.success) {
        setActiveSession(null);
        return { success: true, data: response.data };
      }
      
      return { success: false, message: 'Error al cerrar sesión' };
    } catch (err) {
      console.error('Error closing session:', err);
      const errorMsg = err.response?.data?.message || 'Error al cerrar la sesión';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Registrar un retiro
   */
  const registerWithdrawal = async (sessionId, data) => {
    try {
      const response = await cashSessionService.registerWithdrawal(sessionId, data);
      
      if (response.success) {
        // Actualizar la sesión activa con los nuevos datos
        await loadActiveSession();
        return { success: true };
      }
      
      return { success: false };
    } catch (err) {
      console.error('Error registering withdrawal:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || 'Error al registrar retiro' 
      };
    }
  };

  /**
   * Registrar un ingreso
   */
  const registerIncome = async (sessionId, data) => {
    try {
      const response = await cashSessionService.registerIncome(sessionId, data);
      
      if (response.success) {
        // Actualizar la sesión activa con los nuevos datos
        await loadActiveSession();
        return { success: true };
      }
      
      return { success: false };
    } catch (err) {
      console.error('Error registering income:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || 'Error al registrar ingreso' 
      };
    }
  };

  /**
   * Refrescar la sesión activa
   */
  const refresh = useCallback(() => {
    loadActiveSession();
  }, [loadActiveSession]);

  // Cargar sesión al montar el componente
  useEffect(() => {
    loadActiveSession();
  }, [loadActiveSession]);

  return {
    activeSession,
    loading,
    error,
    hasActiveSession: !!activeSession,
    openSession,
    closeSession,
    registerWithdrawal,
    registerIncome,
    refresh,
  };
};

export default useCashSession;